import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { STEPS_MAP, VALIDATION_PER_STEP } from './LongTermWorkflow.constants';
import { longTermValidation } from './LongTermWorkflow.validation';
import {
  FormFields,
  Step,
  StepKey,
  UseLongTermWorkflowActions,
  UseLongTermWorkflowData,
} from './LongTermWorkflow.types';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import { FinancialGoal } from '../../AutoAccountWorkflow.types';
import { hasErrorsOnCurrentFields } from '../../utils/hasErrorsOnCurrentFields';
import { useLongTermMutation } from '../../api/useLongTermMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { AutomatedAccountsDTO, WhoIsForOptionsDTO } from '../../api/useAutomatedAccountsQuery';
import { formatData } from '../../utils/formatData';

interface Params {
  onSuccess(): void;
  data: AutomatedAccountsDTO;
}

interface UseLongTermWorkflow {
  data: UseLongTermWorkflowData;
  actions: UseLongTermWorkflowActions;
}

export const useLongTermWorkflow = ({ onSuccess, data }: Params): UseLongTermWorkflow => {
  const formBag = useForm<FormFields>({
    defaultValues: formatData(data.longTerm),
    resolver: yupResolver(longTermValidation),
  });

  const { watch, trigger, formState, handleSubmit } = formBag;

  const watchWhoIsFor = watch('whoIsFor');

  const { mutateAsync: saveLongTermForm, isLoading } = useLongTermMutation();

  const steps = Object.entries(STEPS_MAP).filter(([, step]) => step({ whoIsFor: watchWhoIsFor }));

  const {
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    progress,
    step,
    stepKey,
  } = useStepNavigation<Step, StepKey>(steps);

  const CurrentStep = step({ whoIsFor: watchWhoIsFor });

  const showNextButton = stepKey !== StepKey.WhoIsFor;

  const onSubmit = async formData => {
    const isSingleOptionSelected = watch('whoIsFor') === WhoIsForOptionsDTO.Single;
    const { partnerFirstName, partnerLastName, partnerBirthDate, ...restData } = formData;
    const partnerData = {
      partnerFirstName,
      partnerLastName,
      partnerBirthDate,
    };

    const body = {
      financialGoal: FinancialGoal.LongTerm,
      ...(isSingleOptionSelected ? {} : partnerData),
      ...restData,
    };

    try {
      await saveLongTermForm({ body });
      onSuccess();
    } catch (err) {
      toast.error('There was an error submitting the form. Please try again later.');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const validateCurrentStep = async () => {
    const currentStepValidationFields = VALIDATION_PER_STEP[stepKey];
    await trigger(currentStepValidationFields);

    const { errors } = formState;

    return hasErrorsOnCurrentFields(errors, currentStepValidationFields);
  };

  return {
    data: {
      CurrentStep,
      isFirstStep,
      isLastStep,
      progress,
      isLoading,
      showNextButton,
    },
    actions: {
      previousStep,
      nextStep,
      onSubmit: handleSubmit(onSubmit),
      formBag,
      validateCurrentStep,
    },
  };
};
